import { getFromLocalStorage } from '@/local-storage/utils';

export const DEFAULT_DATA_LAYER_NAME = 'dataLayer';
export const DEFAULT_DOMAIN = 'https://www.googletagmanager.com';
export const DEFAULT_SCRIPT_NAME = 'gtm.js';

export type GTMEnvironment = {
  gtm_auth: string;
  gtm_preview: string;
};

export type GTMConfig = {
  dataLayer?: Record<string, unknown>[];
  dataLayerName?: string;
  environment?: GTMEnvironment;
  nonce?: string;
  id: string;
  injectScript?: boolean;
  customDomain?: string;
  customScriptName?: string;
};

export function initGTM({
  dataLayer,
  dataLayerName,
  environment,
  nonce,
  id,
  customDomain,
  customScriptName,
}: GTMConfig & {
  dataLayerName: NonNullable<GTMConfig['dataLayerName']>;
  customDomain: NonNullable<GTMConfig['customDomain']>;
  customScriptName: NonNullable<GTMConfig['customScriptName']>;
}): void {
  const { getDataLayerScript, getScript, getNoScript } = setupGTM({
    dataLayer,
    dataLayerName,
    environment,
    nonce,
    id,
    customDomain,
    customScriptName,
  });

  const dataLayerScript = getDataLayerScript();
  const script = getScript();
  const noScript = getNoScript();

  document.head.insertBefore(dataLayerScript, document.head.childNodes[0] ?? null);
  document.head.insertBefore(script, document.head.childNodes[1] ?? null);
  document.body.insertBefore(noScript, document.body.childNodes[0] ?? null);
}

function setupGTM(
  config: GTMConfig & {
    dataLayerName: NonNullable<GTMConfig['dataLayerName']>;
    customDomain: NonNullable<GTMConfig['customDomain']>;
    customScriptName: NonNullable<GTMConfig['customScriptName']>;
  }
): {
  getDataLayerScript(): HTMLElement;
  getScript(): HTMLElement;
  getNoScript(): HTMLElement;
} {
  const getDataLayerScript = (): HTMLElement => {
    const dataLayerScript = document.createElement('script');

    if (config.nonce) {
      dataLayerScript.setAttribute('nonce', config.nonce);
    }

    dataLayerScript.innerHTML = getDataLayerSnippet(config.dataLayer, config.dataLayerName);

    return dataLayerScript;
  };

  const getScript = (): HTMLElement => {
    const script = document.createElement('script');

    if (config.nonce) {
      script.setAttribute('nonce', config.nonce);
    }

    script.innerHTML = getScriptSnippet(
      config.dataLayerName,
      config.id,
      config.environment,
      config.customDomain,
      config.customScriptName
    );

    return script;
  };

  const getNoScript = (): HTMLElement => {
    const noScript = document.createElement('noscript');

    noScript.innerHTML = getNoScriptSnippet(config.id, config.environment, config.customDomain);

    return noScript;
  };

  return {
    getDataLayerScript,
    getNoScript,
    getScript,
  };
}

function getDataLayerSnippet(
  dataLayer: GTMConfig['dataLayer'],
  dataLayerName: GTMConfig['dataLayerName']
): string {
  return `window.${dataLayerName} = window.${dataLayerName} || [];${
    dataLayer ? `window.${dataLayerName}.push(${JSON.stringify(dataLayer)})` : ''
  }`;
}

function getScriptSnippet(
  dataLayerName: GTMConfig['dataLayerName'],
  id: GTMConfig['id'],
  environment?: GTMEnvironment,
  customDomain: GTMConfig['customDomain'] = DEFAULT_DOMAIN,
  customScriptName: GTMConfig['customScriptName'] = DEFAULT_SCRIPT_NAME
): string {
  let params = '';

  if (environment) {
    const { gtm_auth, gtm_preview } = environment;
    params = `+"&gtm_auth=${gtm_auth}&gtm_preview=${gtm_preview}&gtm_cookies_win=x"`;
  }

  return `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      '${customDomain}/${customScriptName}?id='+i+dl${params};f.parentNode.insertBefore(j,f);
    })(window,document,'script','${dataLayerName}','${id}');
  `;
}

function getNoScriptSnippet(
  id: GTMConfig['id'],
  environment?: GTMEnvironment,
  customDomain: GTMConfig['customDomain'] = DEFAULT_DOMAIN
): string {
  let params = '';

  if (environment) {
    const { gtm_auth, gtm_preview } = environment;
    params = `&gtm_auth=${gtm_auth}&gtm_preview=${gtm_preview}&gtm_cookies_win=x`;
  }

  return `<iframe src="${customDomain}/ns.html?id=${id}${params}" height="0" width="0" style="display:none;visibility:hidden" id="tag-manager"></iframe>`;
}

export type SendToDataLayerParams = {
  dataLayerName: GTMConfig['dataLayerName'];
  data: Record<string, unknown>;
};

export function sendToDataLayer({
  dataLayerName,
  data,
}: SendToDataLayerParams & {
  dataLayerName: NonNullable<GTMConfig['dataLayerName']>;
}): void {
  const usageAnalyticsEnabled = getFromLocalStorage('usage-analytics-enabled');

  // if usage analytics are not enabled, don't send any data
  if (usageAnalyticsEnabled !== 'true') return;

  const dataLayer = (window as unknown as Record<string, Record<string, unknown>[]>)[dataLayerName];

  if (!dataLayer) {
    console.warn(`dataLayer ${dataLayerName} does not exist, make sure to initialize it`);
    return;
  }

  dataLayer.push(data);
}
